import {
	type HTTPErrorCallback,
	HTTPMethod,
	type HTTPRequestConfig,
	type HTTPRequestHook,
	type ProxyResult,
} from "./types";

function unlisten<T>(listener: T, listeners: T[]) {
	const listenerIndex = listeners.findIndex((l) => l === listener);
	if (listenerIndex !== -1) {
		listeners.splice(listenerIndex, 1);
	}
}

export class HTTPResponseError extends Error {
	readonly status: number;
	readonly response: any;

	#handled = false;

	constructor(status: number, response: any) {
		super(JSON.stringify(response));

		this.status = status;
		this.response = response;
	}

	isHandled() {
		return this.#handled;
	}

	markHandled() {
		this.#handled = true;
	}
}

export abstract class HTTPService<TRES extends { ok: boolean; status: number } = any> {
	#requestHooks: HTTPRequestHook[] = [];
	#errorListeners: HTTPErrorCallback[] = [];
	#unhandledErrors: HTTPResponseError[] = [];

	readonly baseUrl: string;

	constructor(public baseURL: string) {
		this.baseUrl = baseURL;
	}

	/**
	 * Register a callback that get called every time an error occure
	 * @param cb The callback when an error occure
	 * @param processUnhandled Will trigger the callback for each unprocessed errors
	 * @returns The unsubcribe function
	 */
	onError(cb: HTTPErrorCallback, processUnhandled = false) {
		this.#errorListeners.push(cb);

		if (processUnhandled) {
			this.#unhandledErrors.forEach((error) => cb(error));
			this.#unhandledErrors = [];
		}

		return () => {
			unlisten(cb, this.#errorListeners);
		};
	}

	/**
	 * Register a callback that get called every time a request occure
	 * @param cb The callback hook
	 * @returns The unsubcribe function
	 */
	onRequest(cb: HTTPRequestHook) {
		this.#requestHooks.push(cb);

		return () => {
			unlisten(cb, this.#requestHooks);
		};
	}

	async get<T = any>(resource: string, options?: Partial<HTTPRequestConfig>) {
		return this.executeRequest<T>(HTTPMethod.GET, resource, null, options);
	}

	async head(resource: string, options?: Partial<HTTPRequestConfig>) {
		return this.executeRequest(HTTPMethod.HEAD, resource, null, options);
	}

	async delete<T = any>(resource: string, options?: Partial<HTTPRequestConfig>) {
		return this.executeRequest<T>(HTTPMethod.DELETE, resource, null, options);
	}

	async post<T = any>(
		resource: string,
		data?: Record<string, any>,
		options?: Partial<HTTPRequestConfig>
	) {
		return this.executeRequest<T>(HTTPMethod.POST, resource, data, options);
	}

	async postFile<T = any>(
		resource: string,
		fileName: string,
		file: File,
		options?: Partial<HTTPRequestConfig>
	) {
		const form = new FormData();
		form.append(fileName, file);

		return this.executeRequest<T>(HTTPMethod.POST, resource, form, options);
	}

	async put<T = any>(resource: string, data?: any, options?: Partial<HTTPRequestConfig>) {
		return this.executeRequest<T>(HTTPMethod.PUT, resource, data, options);
	}

	async patch<T = any>(resource: string, data?: any, options?: Partial<HTTPRequestConfig>) {
		return this.executeRequest<T>(HTTPMethod.PATCH, resource, data, options);
	}

	async executeRequest<T>(
		method: HTTPMethod,
		resource: string,
		data?: any,
		options: Partial<HTTPRequestConfig> = {}
	): Promise<T> {
		let config = this.#buildConfig(method, data, options);

		for (const hook of this.#requestHooks) {
			config = await hook(resource, config);
		}

		const url = this.#buildUrl(resource, config);

		const response = await this.executeHTTPCall(url, config);
		if (response.ok) {
			if (method === HTTPMethod.HEAD) {
				return undefined as T;
			}

			return this.parseBody(response);
		} else {
			let body: any;
			if (method === HTTPMethod.HEAD) {
				body = undefined;
			} else {
				body = await this.parseBody(response);
			}

			const err = new HTTPResponseError(response.status, body);

			if (!config.ignoreError) {
				this.#sendError(err);
			}

			throw err;
		}
	}

	async withResponseProxy<T>(request: Promise<T>): Promise<ProxyResult<T>> {
		try {
			const result = await request;
			return {
				status: "SUCCESS",
				data: result,
			};
		} catch {
			return {
				status: "FAILED",
			};
		}
	}

	#buildUrl(resource: string, config: HTTPRequestConfig): string {
		let url: string;

		if (resource.startsWith("http")) {
			url = resource;
		} else {
			url = `${this.baseUrl}/${resource.replace(/^\/+/, "")}`;
		}

		if (config.params && Object.keys(config.params).length !== 0) {
			const params = new URLSearchParams(JSON.parse(JSON.stringify(config.params)));
			params.forEach((val, key) => {
				if (val === "" || val == "undefined") {
					params.delete(key);
				}
			});
			url += `?${params.toString()}`;
		}

		return url;
	}

	#buildConfig(
		method: HTTPMethod,
		data: FormData | Record<string, any> | null = null,
		config: Partial<HTTPRequestConfig> = {}
	): HTTPRequestConfig {
		config.method = method;

		config.headers = config.headers || {};
		config.params = config.params || {};

		if (data) {
			this.encodeBody(data, config as HTTPRequestConfig);
		}

		// if (config.version != null) {
		// 	config.headers["X-API-Version"] = config.version.toString();
		// }

		return config as HTTPRequestConfig;
	}

	#sendError(error: HTTPResponseError) {
		if (error == null) {
			return;
		}

		if (this.#errorListeners.length === 0) {
			this.#unhandledErrors.push(error);
		} else {
			const listeners = this.#errorListeners;

			// Reverse order, so new handler can mark the error as handled.
			for (let i = listeners.length - 1; i >= 0; i--) {
				if (error.isHandled()) {
					return;
				}
				this.#errorListeners[i](error);
			}
		}
	}

	abstract encodeBody(data: FormData | Record<string, any> | null, config: HTTPRequestConfig): void;
	abstract executeHTTPCall(url: string, config: HTTPRequestConfig): Promise<TRES>;
	abstract parseBody<T>(response: TRES): Promise<T>;
}
